/**
 * Shared cache invalidation logic for post-transaction updates
 *
 * Strategy:
 * - refetchQueries(): For data user is actively watching (balance, prices, position)
 * - invalidateQueries(): For background data that will refresh when accessed
 * - Delayed refetch: RPC nodes may lag behind the block where tx was mined,
 *   so we refetch again after a short delay to ensure fresh data
 *
 * Note: Convex queries (indexer data, price history) are reactive and auto-update.
 * Only RPC-based queries (prices, balance, shares) need manual invalidation.
 *
 * Buy/Sell changes on-chain:
 * - _quantities[bucket] → affects LMSR prices
 * - _userShares[key] → user's position
 * - _totalUserShares[bucket] → top holders
 * - USDC transfers → balances
 *
 * Claim changes on-chain (post-finalization only):
 * - _claimedBitmap → marks user as claimed
 * - USDC transfer → user receives winnings
 * - Does NOT change: prices, shares, quantities (market is frozen)
 */

import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useRef } from "react"
import type { Address } from "viem"
import { marketKeys } from "../market/useMarket"
import { walletKeys } from "../wallet/useUsdcBalance"

/** Delays before refetches to handle RPC lag (ms) - 2s, 5s, 10s */
const REFETCH_DELAYS_MS = [2000, 5000, 10000] as const

// Query keys for stats - minimal since most indexer data is reactive
const statsKeys = {
	all: ["stats"] as const,
	weekMarketData: (weekId: string) => [...statsKeys.all, "weekMarketData", weekId] as const,
	allWeekMarketData: () => [...statsKeys.all, "weekMarketData"] as const,
}

export function useInvalidateAfterTransaction() {
	const queryClient = useQueryClient()
	// Track pending delayed refetch timeouts so we can cancel on unmount or new trade
	const delayedRefetchTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])

	// Invalidates RPC-based market queries affected by trades
	const invalidateMarketForTrade = useCallback(
		({ marketAddress, userAddress }: { marketAddress: Address; userAddress?: Address }) => {
			queryClient.invalidateQueries({
				queryKey: marketKeys.marketPrices(marketAddress),
			})
			queryClient.invalidateQueries({
				queryKey: marketKeys.marketBalance(marketAddress),
			})
			if (userAddress) {
				queryClient.invalidateQueries({
					queryKey: marketKeys.marketShares(marketAddress, userAddress),
				})
			}
		},
		[queryClient],
	)

	// Week market data uses an action (not reactive) so needs invalidation
	const invalidateWeekData = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: statsKeys.allWeekMarketData(),
		})
	}, [queryClient])

	// Invalidate user wallet data
	const invalidateUserData = useCallback(
		({
			userAddress,
			balance = true,
			positions = true,
		}: {
			userAddress: Address
			balance?: boolean
			positions?: boolean
		}) => {
			if (balance) {
				queryClient.invalidateQueries({
					queryKey: walletKeys.usdcBalance(userAddress),
				})
			}
			if (positions) {
				queryClient.invalidateQueries({
					queryKey: marketKeys.allUserPositions(userAddress),
				})
			}
		},
		[queryClient],
	)

	// After buy/sell: immediate refetch for visible UI, background invalidate for less critical data
	// Also schedules delayed refetches to handle RPC node lag
	const invalidateAfterTrade = useCallback(
		({ marketAddress, userAddress }: { marketAddress: Address; userAddress?: Address }) => {
			// Cancel any pending delayed refetches from previous trade
			for (const timeout of delayedRefetchTimeoutsRef.current) {
				clearTimeout(timeout)
			}
			delayedRefetchTimeoutsRef.current = []

			// Helper to refetch RPC-based queries
			const refetchRpcQueries = () => {
				queryClient.refetchQueries({ queryKey: marketKeys.marketPrices(marketAddress) })
				if (userAddress) {
					queryClient.refetchQueries({ queryKey: walletKeys.usdcBalance(userAddress) })
					queryClient.refetchQueries({
						queryKey: marketKeys.marketShares(marketAddress, userAddress),
					})
				}
			}

			// Immediate refetch: user is watching these on market page
			refetchRpcQueries()

			// Background: invalidate for next access
			queryClient.invalidateQueries({ queryKey: marketKeys.marketBalance(marketAddress) })
			invalidateWeekData()
			if (userAddress) {
				invalidateUserData({ userAddress, balance: false, positions: true })
			}

			// Delayed refetches: RPC nodes may lag behind the mined block
			for (const delayMs of REFETCH_DELAYS_MS) {
				const timeout = setTimeout(refetchRpcQueries, delayMs)
				delayedRefetchTimeoutsRef.current.push(timeout)
			}

			// Note: Price history and indexer data are Convex queries (reactive) - no invalidation needed
		},
		[invalidateWeekData, invalidateUserData, queryClient],
	)

	// After claim: only affects balance and claimed status (market is frozen, no price/share changes)
	const invalidateAfterClaim = useCallback(
		({ marketAddress, userAddress }: { marketAddress: Address; userAddress: Address }) => {
			// Cancel any pending delayed refetches from previous action
			for (const timeout of delayedRefetchTimeoutsRef.current) {
				clearTimeout(timeout)
			}
			delayedRefetchTimeoutsRef.current = []

			// Helper to refetch claim-related queries
			const refetchClaimQueries = () => {
				queryClient.refetchQueries({ queryKey: walletKeys.usdcBalance(userAddress) })
				queryClient.refetchQueries({
					queryKey: marketKeys.marketHasClaimed(marketAddress, userAddress),
				})
			}

			// Immediate refetch
			refetchClaimQueries()

			// Background invalidation
			queryClient.invalidateQueries({ queryKey: marketKeys.allUserPositions(userAddress) })

			// Delayed refetches: RPC nodes may lag behind the mined block
			for (const delayMs of REFETCH_DELAYS_MS) {
				const timeout = setTimeout(refetchClaimQueries, delayMs)
				delayedRefetchTimeoutsRef.current.push(timeout)
			}
		},
		[queryClient],
	)

	// After withdraw: only affects balance (no market/position changes)
	const invalidateAfterWithdraw = useCallback(
		({ userAddress }: { userAddress: Address }) => {
			// Cancel any pending delayed refetches from previous action
			for (const timeout of delayedRefetchTimeoutsRef.current) {
				clearTimeout(timeout)
			}
			delayedRefetchTimeoutsRef.current = []

			// Helper to refetch balance
			const refetchBalance = () => {
				queryClient.refetchQueries({ queryKey: walletKeys.usdcBalance(userAddress) })
			}

			// Immediate refetch
			refetchBalance()

			// Delayed refetches: RPC nodes may lag behind the mined block
			for (const delayMs of REFETCH_DELAYS_MS) {
				const timeout = setTimeout(refetchBalance, delayMs)
				delayedRefetchTimeoutsRef.current.push(timeout)
			}
		},
		[queryClient],
	)

	return {
		invalidateMarketForTrade,
		invalidateWeekData,
		invalidateUserData,
		invalidateAfterTrade,
		invalidateAfterClaim,
		invalidateAfterWithdraw,
	}
}
